import type { ComponentType } from 'react';
import type { FallbackProps } from 'react-error-boundary';

import { FetchError } from '@frontend/api';

import { PaneBodyFallback } from './PaneBodyFallback';
import { PaneNoAccessState } from './PaneNoAccessState';

/**
 * Backend views raise a 403 with this error code (see paperless_web's
 * CustomAPIException) when the user has no access-granting permission in
 * any of their visible user groups, as opposed to other errors.
 */
const NO_ACCESS_ERROR_CODE = 'no_access';

export const isNoAccessError = (error: unknown): boolean =>
  error instanceof FetchError && error.code === NO_ACCESS_ERROR_CODE;

/**
 * Builds a stable fallback component for `PaneErrorBoundary` that renders
 * `PaneNoAccessState` for the pane's "no access anywhere" 403, and falls
 * back to the default `PaneBodyFallback` (generic error + retry) for
 * everything else. Create once per pane at module scope (not inline in
 * render) so the error boundary keeps a stable fallback identity.
 */
export const createPaneAccessAwareFallback = (
  resourceLabel: string,
): ComponentType<FallbackProps> => {
  const PaneAccessAwareFallback = (props: FallbackProps) =>
    isNoAccessError(props.error) ? (
      <PaneNoAccessState resourceLabel={resourceLabel} />
    ) : (
      <PaneBodyFallback {...props} />
    );
  PaneAccessAwareFallback.displayName = 'PaneAccessAwareFallback';
  return PaneAccessAwareFallback;
};
